import Button from 'components/buttons/button/Button';
import styles from "./CustomSearchBox.module.scss";
import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  //useHits,
  useSearchBox,
  UseSearchBoxProps
} from 'react-instantsearch';
import { useAutocomplete } from "hooks";
//import { ResetButton } from 'components/buttons/resetButton/ResetButton';
import { getAllClassNames } from 'helpers';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { ResetButton } from 'components/buttons/resetButton/ResetButton';
import { HighlightText } from 'components/highlightText/HighlightText';

interface ExtendedSearchBoxProps extends UseSearchBoxProps {
  color1?: string;
  color2?: string;
  btnText?: string;
  label?: string;
  className?: string;
  onSubmit?: (query: string) => void;
  showMagnifier?: boolean;
  isAutoComplete?: boolean;
  placeholder?: string;
  indexName?: string;
  onEmptySubmit?: (valid: boolean) => void;
  onGetQuery?: (query: string) => void;
  style?: React.CSSProperties;
  styleInput?: React.CSSProperties;
  customLabelClass?: string;
  useStyleButton?: boolean;
  restrictSearchableAttributes?: string[];
  filterSearch?: string;
  enableOnlyAlphanumeric?: boolean;
  onSearchStateChange?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CustomSearchBox = forwardRef((props: ExtendedSearchBoxProps, ref) => {
  const { query, refine } = useSearchBox(props);
  const { color1, color2, btnText, label, className, onSubmit, showMagnifier, isAutoComplete, placeholder, onEmptySubmit, onGetQuery, restrictSearchableAttributes, style, styleInput, customLabelClass, useStyleButton, filterSearch, enableOnlyAlphanumeric, onSearchStateChange, onChange } = props;
  const [inputValue, setInputValue] = useState(query);
  const results = useAutocomplete(inputValue, filterSearch ? filterSearch : undefined, props.indexName, restrictSearchableAttributes);
  const inputRef = useRef<HTMLInputElement>(null);
  const [fromSelect, setFromselect] = useState(false);
  const [fromSubmit, setFromSubmit] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);
  const [clickedOutSide, setClickOutSide] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const magnifierBtnRef = useRef<HTMLButtonElement>(null);
  const [ignoreSearch, setIgnoreSearch] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        showDropDown
      ) {
        setShowDropDown(false);
        setClickOutSide(true)
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropDown]);

  useEffect(() => {    
    if (results.hits.length && !fromSelect && !fromSubmit && !clickedOutSide && hasUserInteracted) {
      setShowDropDown(true);
    }
  }, [results, hasUserInteracted]);

  const handleReset = () => {
    setInputValue('');
    //refine(''); // Clear the Algolia query
    //onGetQuery && onGetQuery(''); // Notify parent component about the reset
  };

  function setQuery(newQuery: string) {
    if (newQuery.length < 3) {
      setShowDropDown(false);
      return;
    }
    setInputValue(newQuery);
    refine(newQuery);
  }

  const handleSubmit = (): void => {
    setShowDropDown(false);
    onSubmit && onSubmit(inputValue);
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[A-Za-zא-ת0-9()\s\-]+$/;
    if (enableOnlyAlphanumeric && event.currentTarget.value !== '' && !regex.test(event.currentTarget.value)) return;
    setClickOutSide(false)
    setInputValue(event.currentTarget.value);
    onGetQuery && onGetQuery(event.currentTarget.value);
    //refine(event.currentTarget.value);
    setFromSubmit(false);
    onChange && onChange(event);
    setHasUserInteracted(true); // Mark as interacted
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showDropDown || !results.hits.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev < results.hits.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0) {
          const hit = results.hits[selectedIndex];
          const title = hit.title || hit.hebrewName || hit.name || hit.serviceName;
          handleItemSelect(title);
        }
        break;
      case 'Escape':
        setShowDropDown(false);
        break;
    }
  };

  const formRef = useRef<HTMLFormElement>(null);

  const handleItemSelect = (title: string) => {
    setSelectedIndex(-1);
    setFromselect(true);
    setInputValue(title);
    setShowDropDown(false);
    setFromSubmit(true);
    handleSubmit();
    onGetQuery && onGetQuery(title);
    setTimeout(() => {
      btnRef.current?.click();
      magnifierBtnRef.current?.click();
    }, 1000);
  }
  useImperativeHandle(ref, () => ({
    invokeChildFunction: setQuery,
    submitForm: () => {
      if (formRef.current) {
        // Programmatically submit the form
        formRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    },
    clearInput: () => {
      setIgnoreSearch(true); // Ignore the search query
      setInputValue(''); // Clear the input value
      refine(''); // Clear the Algolia query
      if (inputRef.current) {
        inputRef.current.value = ''; // Explicitly clear the DOM input field
      }
    },
  }));

  return (
    <div
      className={styles.container}
    >
      <form
        className={styles.searchboxWrapper}
        action=""
        role="search"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (inputRef.current && inputRef.current.value.length > 0 || ignoreSearch) {
            if (inputRef.current) {
              setQuery(inputRef.current.value);
            }
            inputRef.current?.blur();
            setFromselect(false);
            setFromSubmit(true);
            handleSubmit();
            onEmptySubmit && onEmptySubmit(false);
            onGetQuery && inputRef.current && onGetQuery(inputRef.current.value);
            onSearchStateChange && onSearchStateChange();
          }
          else {
            onEmptySubmit && onEmptySubmit(true);
          }
        }}
        ref={formRef}
        onReset={() => handleReset()}
      >
        <div className={getAllClassNames([styles.inputWrapper, className ? className : ''])}>{label && <label htmlFor='searchbox' className={getAllClassNames([styles.text, customLabelClass && styles[customLabelClass]])}>{label}</label>}
          <input
            className={getAllClassNames(['ais-SearchBox-input', styles.input])}
            style={styleInput}
            ref={inputRef}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={512}
            type="search"
            defaultValue={inputValue}
            value={inputValue}
            placeholder={placeholder}
            id='searchbox'
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            aria-expanded={showDropDown}
            aria-controls="cb1-listbox"
            role="combobox"
          />
          {inputValue && !showMagnifier && <ResetButton
            styleButton={useStyleButton ? { insetInline: 'auto 2rem' } : {}}
            onReset={() => handleReset()}
          />
          }
        </div>
        {!showMagnifier && <Button color1={color1} color2={color2} type="submit" text='חיפוש' className={styles.buttonSearch} ref={btnRef}>{btnText}</Button>}
        {showMagnifier && <button ref={magnifierBtnRef} type="submit" className={styles.magnifier}><SvgGenerator className={styles.svg} name="Search" objectName="zakautSearchResults" /></button>}

        {isAutoComplete && showDropDown && inputRef?.current?.value && Number(inputRef.current.value.length) > 2 && <div id="cb1-listbox" className={`${styles.collapseSuggest} collapse ${results.hits.length ? 'show' : ''}`} style={style} ref={dropdownRef} >
          <ul role="listbox" className={styles.collapseSuggestList} aria-live="polite">
            {props.indexName === 'MEDICINES_INDEX' &&
              results.hits.map((hit, index) => (
                <li
                  className={`${styles.collapseSuggestItem} ${selectedIndex === index ? styles.selected : ''}`}
                  role="option"
                  key={hit.objectID}
                  onClick={() =>
                    handleItemSelect(hit.title || hit.hebrewName || hit.name)
                  }
                  aria-selected={selectedIndex === index}
                  tabIndex={0}
                >
                  {hit.title || hit.hebrewName || hit.name}
                </li>
              ))}
            {props.indexName === 'ZAKAUT_INDEX' &&
              results.hits.map((hit, index) => (
                <li
                  className={`${styles.collapseSuggestItem} ${selectedIndex === index ? styles.selected : ''}`}
                  role="option"
                  key={hit.objectID}
                  onClick={() =>
                    handleItemSelect(hit.serviceName)
                  }
                  aria-selected={selectedIndex === index}
                  tabIndex={0}
                >
                  <HighlightText query={inputValue} text={hit.serviceName} />
                </li>
              ))}
            {props.indexName === 'SITE_INDEX_HE' &&
              results.hits.map((hit, index) => (
                <li
                  className={`${styles.collapseSuggestItem} ${selectedIndex === index ? styles.selected : ''}`}
                  role="option"
                  key={hit.objectID}
                  onClick={() =>
                    handleItemSelect(hit.title)
                  }
                  aria-selected={selectedIndex === index}
                  tabIndex={0}
                >
                  <HighlightText query={inputValue} text={hit.title} />
                </li>
              ))}
          </ul>
        </div>}
      </form>
    </div>
  );
});

export default CustomSearchBox;